import { supabase } from '../../../core/lib/supabase';
import type { OrgKnowledgeBaseRow, OrgTemplateRow, WorkPillForWizard, WorkPillLabel } from './wizardTypes';

const WORKPILL_SELECT = `
  *,
  workpill_knowledge_bases (
    knowledge_bases!workpill_knowledge_bases_knowledge_base_id_fkey (id, name, is_global)
  ),
  workpill_templates (
    templates!workpill_templates_template_id_fkey (id, name, type)
  ),
  workpill_labels (
    labels!workpill_labels_label_id_fkey (id, name, color)
  )
`;

function mapWorkpillRow(item: Record<string, unknown>): WorkPillForWizard {
  const wkb = item.workpill_knowledge_bases as { knowledge_bases?: OrgKnowledgeBaseRow | null }[] | undefined;
  const wpt = item.workpill_templates as { templates?: { id: string; name: string; type?: string | null } | null }[] | undefined;
  const wpl = item.workpill_labels as { labels?: WorkPillLabel | null }[] | undefined;

  const linkedKBs =
    wkb?.map((r) => r.knowledge_bases).filter((x): x is OrgKnowledgeBaseRow => Boolean(x?.id)) ?? [];
  const linkedTemplates =
    wpt?.map((r) => r.templates).filter((x): x is { id: string; name: string; type?: string | null } => Boolean(x?.id)) ??
    [];
  const linkedLabels = wpl?.map((r) => r.labels).filter((x): x is WorkPillLabel => Boolean(x?.id)) ?? [];

  const {
    workpill_knowledge_bases: _a,
    workpill_templates: _b,
    workpill_labels: _c,
    ...rest
  } = item;

  return {
    ...(rest as Omit<WorkPillForWizard, 'linkedKBs' | 'linkedTemplates' | 'linkedLabels'>),
    linkedKBs,
    linkedTemplates,
    linkedLabels,
  };
}

export async function fetchWorkpillsForWizard(): Promise<WorkPillForWizard[]> {
  const { data, error } = await supabase
    .from('workpills')
    .select(WORKPILL_SELECT)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('fetchWorkpillsForWizard:', error);
    throw new Error(error.message || 'Failed to load workpills');
  }
  return (data || []).map((row) => mapWorkpillRow(row as Record<string, unknown>));
}

export async function fetchOrganizationTemplates(organizationId: string): Promise<OrgTemplateRow[]> {
  const { data, error } = await supabase
    .from('templates')
    .select('id, name, type, content_url, pdf_url')
    .eq('organization_id', organizationId)
    .order('name');

  if (error) {
    console.error('fetchOrganizationTemplates:', error);
    throw new Error(error.message || 'Failed to load templates');
  }
  return (data || []) as OrgTemplateRow[];
}

export async function fetchOrganizationKnowledgeBases(organizationId: string): Promise<OrgKnowledgeBaseRow[]> {
  const { data, error } = await supabase
    .from('knowledge_bases')
    .select('id, name, is_global')
    .eq('organization_id', organizationId)
    .eq('is_global', true)
    .order('name');

  if (error) {
    console.error('fetchOrganizationKnowledgeBases:', error);
    throw new Error(error.message || 'Failed to load knowledge bases');
  }
  return (data || []) as OrgKnowledgeBaseRow[];
}

export async function validateSelectedTemplatesHaveFiles(templateIds: string[]): Promise<boolean> {
  if (templateIds.length === 0) return true;
  const { data, error } = await supabase.from('templates').select('id, content_url, pdf_url').in('id', templateIds);

  if (error) {
    console.error('validateSelectedTemplatesHaveFiles:', error);
    return false;
  }

  const byId = new Map((data || []).map((t) => [t.id as string, t]));
  for (const id of templateIds) {
    const row = byId.get(id) as { content_url?: string | null; pdf_url?: string | null } | undefined;
    if (row && !row.content_url && !row.pdf_url) {
      return false;
    }
  }
  return true;
}
